import type { TranslationKeys } from '../types';

export const en: TranslationKeys = {
  nav: {
    home: 'Home',
    about: 'About',
    faq: 'FAQ',
    pricing: 'Pricing',
    howToUse: 'How to Use',
    giftStore: 'Gift Store',
    blog: 'Blog',
    login: 'Log In',
    signup: 'Sign Up',
    dashboard: 'Dashboard',
    logout: 'Log Out',
  },
  hero: {
    tagline: 'Express love now, not after.',
    headline: 'Say What You Always Wanted to Say.',
    subheadline: 'Express what\'s in your heart — safely, honestly, and in your own way.',
    cta: 'Write Your First Message',
    ctaSecondary: 'See How It Works',
    socialProof: 'heartfelt messages shared',
  },
  values: {
    title: 'Why Living Announcements?',
    problem: {
      title: 'The Words That Matter Most',
      description: 'We often carry our deepest words with us, unsaid. Say It NOW helps you express love and gratitude in your own time, in your own way.',
    },
    solution: {
      title: 'Beautiful, Shareable Memories',
      description: 'Create magazine-style digital messages with photos and heartfelt words that your loved ones can treasure.',
    },
    impact: {
      title: 'Make Every Moment Count',
      description: 'Turn your feelings into stunning visual stories that celebrate the people you love.',
    },
  },
  templates: {
    title: 'Stunning Templates for Every Story',
    subtitle: 'Choose from beautifully designed layouts that bring your words to life.',
    classicElegance: 'Classic Elegance',
    modernWarmth: 'Modern Warmth',
    boldStatement: 'Bold Statement',
    intimateJournal: 'Intimate Journal',
  },
  howItWorks: {
    title: 'How It Works',
    step1: { title: 'Choose Your Photos', description: 'Select 1-3 meaningful photos that capture your relationship.' },
    step2: { title: 'Write Your Message', description: 'Express your love with guided prompts that help you find the right words.' },
    step3: { title: 'Pick a Template', description: 'Choose from 4 stunning magazine-style layouts.' },
    step4: { title: 'Share the Love', description: 'Send your Living Announcement via link or social media.' },
  },
  howToUse: {
    title: 'How to Use Say It NOW',
    subtitle: 'Discover how easy it is to share love and gratitude with those who matter most',
    create: {
      title: 'Create Your Living Announcement',
      subtitle: 'Express love in 5 simple steps',
      step1: { label: 'Upload Photos', desc: 'Add up to 3 meaningful photos' },
      step2: { label: 'Choose Relationship', desc: 'Parent, grandparent, friend, and more' },
      step3: { label: 'Write Your Message', desc: 'AI can help you find the right words' },
      step4: { label: 'Pick a Template', desc: 'Classic, Modern, Bold, or Journal' },
      step5: { label: 'Preview & Publish', desc: 'Share with a private or public link' },
    },
    dashboard: {
      title: 'Manage Your Announcements',
      subtitle: 'Your personal dashboard',
      viewAll: { label: 'View All', desc: 'See all your living announcements in one place' },
      editAnytime: { label: 'Edit Anytime', desc: 'Update photos, messages, and templates' },
      copyLinks: { label: 'Copy Links', desc: 'Share your announcements easily' },
      viewAsMagazine: 'View as Magazine',
    },
    aiFeatures: {
      title: 'AI-Powered Features',
      subtitle: 'Let technology help you stay connected',
      messageWriting: { label: 'AI Message Writing', desc: 'Stuck for words? AI helps craft heartfelt messages' },
      reminders: { label: 'Heartfelt Reminders', desc: "Gentle nudges when you haven't connected in a while" },
      autoCompose: { label: 'Auto-Compose', desc: 'AI generates announcements for your approval' },
    },
    gamification: {
      title: 'Earn Badges & Level Up',
      subtitle: 'Celebrate your journey of love',
      levelsTitle: 'Progress Through Levels',
      badgesTitle: 'Collect Badges',
      levels: {
        heartOpener: 'Heart Opener',
        soulConnector: 'Soul Connector',
        loveAmbassador: 'Love Ambassador',
        gratitudeChampion: 'Gratitude Champion',
        legacyMaker: 'Legacy Maker',
      },
      badges: {
        firstExpression: 'First Expression',
        loveAmbassador: 'Love Ambassador',
        birthdayChampion: 'Birthday Champion',
        consistencyStar: 'Consistency Star',
      },
      jacKeyLines: {
        firstExpression: 'Showing up once can change everything.',
        relationshipCelebrant: 'Some relationships deserve words said out loud.',
        birthdayChampion: 'Remembering dates is one way love shows up.',
        friendshipKeeper: 'Long friendships are built by being remembered.',
        consistencyStar: 'Connection grows through quiet consistency.',
        loveAmbassador: "You're choosing presence again and again.",
      },
      helperMicrocopy: "Tip: Hover over any badge to see how it's earned.",
      progressContext: {
        nextMilestone: 'Next milestone: {points} point(s) to {level}.',
        maxLevel: "You've built something lasting.",
      },
      yourBadges: 'Your Badges',
      pointsTotal: '{points} points total',
      ptsToGo: '{points} pts to go',
      achieved: 'Achieved!',
      next: 'Next:',
      progressEncouragement: "You're making progress! Keep expressing love to unlock more badges.",
      quietModeOn: 'Quiet Mode is on',
      tiers: { awareness: 'Awareness', presence: 'Presence', continuity: 'Continuity', beyond: 'Beyond' },
      ptsTo: '{points} pts to {tier}',
      highestTier: "You've reached the highest tier!",
      softEncouragement: {
        message: "Many people realize there's more they want to say.",
        cta: 'Continue the conversation',
        dismiss: 'Maybe later',
      },
    },
  },
  examples: {
    title: 'See Living Announcements in Action',
    subtitle: 'Real messages of love, beautifully presented. Swipe through to see the impact of expressing gratitude now.',
    to: 'To',
    from: 'With love',
  },
  video: {
    badge: 'Our Community',
    title: 'Why Say It NOW Matters',
    subtitle: 'Real people, real stories. See yourself in the movement to express love while it matters most.',
    playButton: 'Watch Our Story',
    stat1: 'Messages Shared',
    stat2: 'Countries Reached',
    stat3: 'Lives Touched',
  },
  pricing: {
    title: 'Simple, Transparent Pricing',
    subtitle: 'Choose what supports how you want to show up. Start free, add more anytime.',
    popular: 'Recommended',
    guarantee: '💛 Your words are saved. You can return to them anytime.',
    monthly: 'Monthly',
    annual: 'Annual',
    savePercent: 'Save 20%',
    plans: {
      free: {
        name: 'Say It Once',
        price: '$0',
        period: '/free',
        description: 'One beautiful moment',
        features: ['3 Free announcements', 'Basic magazine-style design', 'Upload 1–3 photos', 'Public or private sharing link', 'Limited AI assist (2 uses)', 'Say It NOW watermark included'],
        cta: 'Start Your Moment',
      },
      plus: {
        name: 'Connection',
        price: '$17',
        period: 'one-time',
        annualPrice: '$17',
        annualPeriod: 'one-time',
        description: 'Show up when it counts',
        features: ['+10 Announcements', 'Everything in Starter', 'Scheduling & email reminders', 'Full AI writing assist', 'Remove watermark'],
        cta: 'Choose Connection',
      },
      legacy: {
        name: 'Legacy',
        price: '$35',
        period: 'one-time',
        annualPrice: '$35',
        annualPeriod: 'one-time',
        description: 'Make it a meaningful practice',
        features: ['+25 Announcements', 'Everything included', 'Recurring annual announcements', 'TRESLOG access', 'Downloadable keepsakes', 'Video messages'],
        cta: 'Choose Legacy',
      },
    },
  },
  cta: {
    badge: 'When you are ready',
    title: 'Share What Matters, In Your Own Time',
    subtitle: 'The people you love deserve to know how you feel. Create your first Living Announcement in minutes — no payment required.',
    button: 'Create Free Announcement',
    viewPricing: 'View Options',
    noCard: '💛 No credit card required • Free plan available',
  },
  footer: {
    tagline: 'Express love now, not after.',
    about: 'About',
    faq: 'FAQ',
    pricing: 'Pricing',
    howToUse: 'How to Use',
    giftStore: 'Gift Store',
    privacy: 'Privacy',
    terms: 'Terms',
    contact: 'Contact',
    copyright: '© 2026 Say It NOW. All rights reserved.',
  },
  auth: {
    loginTitle: 'Welcome Back',
    loginSubtitle: 'Sign in to continue creating Living Announcements',
    signupTitle: 'Join Say It NOW',
    signupSubtitle: 'Start expressing love to those who matter most',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    fullName: 'Full Name',
    loginButton: 'Sign In',
    signupButton: 'Create Account',
    forgotPassword: 'Forgot password?',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    orContinueWith: 'Or continue with',
    google: 'Google',
    apple: 'Apple',
    resetPassword: 'Reset Password',
    resetPasswordSubtitle: 'Enter your email to receive a password reset link',
    sendResetLink: 'Send Reset Link',
    backToLogin: 'Back to login',
    newPassword: 'Set New Password',
    newPasswordSubtitle: 'Enter your new password below',
    newPasswordLabel: 'New Password',
    confirmNewPassword: 'Confirm New Password',
    passwordMismatch: 'Passwords do not match',
    passwordUpdated: 'Password Updated!',
    passwordUpdatedSubtitle: 'Your password has been successfully updated. You can now sign in with your new password.',
    updatePasswordButton: 'Update Password',
    resetLinkExpired: 'This reset link may have expired or already been used. Please request a new one.',
  },
  common: {
    loading: 'Loading...',
    error: 'Something went wrong',
    retry: 'Try Again',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    share: 'Share',
    preview: 'Preview',
    publish: 'Publish',
    draft: 'Draft',
    pending: 'Pending Review',
    approved: 'Approved',
    rejected: 'Rejected',
  },
  about: {
    badge: 'Our Purpose',
    title: 'Why Say It NOW Exists',
    subtitle: 'Because love should be expressed before it becomes a eulogy.',
    story1: 'The whole purpose for creating this app is to provide a better connection between humans. It is often that we do not hear or read how great and awesome a person was until they are gone.',
    story2: "For years, I have seen multiple posts on a person's page who recently passed away, but when you look at the history of their story line, no one ever said those comments to that person. Then people go to social media to post messages to the deceased as if they can read them.",
    story3: 'We do not know if in the afterlife, a spirit actually sees the post... I often wondered.',
    story4: 'This web app gives individuals the opportunity and reminds them to say how they feel about someone while that person is still in the flesh and able to appreciate kind words.',
    story5: 'But life happens and people get busy and forget. Not anymore...',
    highlight: 'Sometimes, people just need to know that there is someone who cares about them and thinks the world of them. This is what someone needs to know to help their mental health.',
    mission: 'Say It NOW was developed so a person can leave this world knowing how loved, appreciated, or great they are.',
    valuesTitle: 'Our Core Values',
    value1Title: 'Express Love Now',
    value1Desc: "Don't wait for a funeral to share how much someone means to you.",
    value2Title: 'Human Connection',
    value2Desc: 'Building bridges between hearts through meaningful words.',
    value3Title: 'Mental Wellness',
    value3Desc: "Knowing you're loved and appreciated improves mental health.",
    value4Title: 'Remembered in Life',
    value4Desc: "Helping everyone know their worth while they can still hear it.",
    ctaTitle: 'Ready to Express Your Love?',
    ctaSubtitle: "Start creating beautiful Living Announcements today. It takes just a few minutes to brighten someone's day.",
    ctaButton: 'Get Started Free',
    ctaSecondary: 'View Pricing',
  },
  faq: {
    badge: 'Help Center',
    title: 'Frequently Asked Questions',
    subtitle: 'Everything you need to know about Say It NOW and how it helps you express love while it matters most.',
    categoryGeneral: 'General Questions',
    categoryFeatures: 'Features & Plans',
    categoryPrivacy: 'Privacy & Sharing',
    categoryBilling: 'Billing & Support',
    categoryBadges: 'Badges, Points & Levels',
    q15: 'How do badges work, and how do I earn them?',
    a15: '',
    badgesFaq: {
      intro: 'Badges are a gentle way we recognize meaningful moments you create on Say It NOW. They are not about competition — they are about showing up, expressing yourself, and staying connected. Every badge earns points, and those points unlock levels over time. You don\'t need to do anything extra — badges are awarded automatically as you use the app.',
      howEarnedTitle: 'How badges are earned',
      howEarnedSubtitle: 'Badges are triggered by specific actions across the site. Here are all currently available badges and how they are earned:',
      badges: {
        firstExpression: { name: 'First Expression', earned: 'Earned when you send your very first Living Announcement.', trigger: 'Triggered after your first publish.' },
        relationshipCelebrant: { name: 'Relationship Celebrant', earned: 'Earned when you create an announcement for your spouse.', trigger: 'Triggered when "Spouse" is selected as the relationship.' },
        birthdayChampion: { name: 'Birthday Champion', earned: 'Earned when you add a birthday to your Important Dates.', trigger: 'Found in Settings → Important Dates.' },
        friendshipKeeper: { name: 'Friendship Keeper', earned: 'Earned when you add a Friendship Anniversary to Important Dates.', trigger: '', note: 'This badge is earned by adding a date in Settings → Important Dates, not by selecting a relationship during announcement creation.' },
        consistencyStar: { name: 'Consistency Star', earned: 'Earned when you send announcements across three or more different weeks.' },
        loveAmbassador: { name: 'Love Ambassador', earned: 'Earned when you send eight or more announcements within fourteen days.' },
      },
      levelsTitle: 'Level progression',
      levelsSubtitle: 'As you earn badge points, you unlock levels. Levels never expire and reflect your journey over time.',
      levels: { heartOpener: 'Heart Opener', soulConnector: 'Soul Connector', loveAmbassador: 'Love Ambassador', gratitudeChampion: 'Gratitude Champion', legacyMaker: 'Legacy Maker' },
      pointsLabel: 'points',
      levelsNote: 'Each level represents continued engagement and recognition. Your ability to write, reflect, and connect is never restricted by your level.',
      dateNoteTitle: 'A helpful note about dates',
      dateNoteBody: 'The Friendship Keeper badge specifically requires adding a Friendship Anniversary in Settings → Important Dates. Selecting "friend" as a relationship when creating an announcement does not trigger this badge.',
      finalNote: 'Badges exist to encourage reflection and connection — not pressure. You don\'t need to chase them. They will appear naturally as you use Say It NOW in ways that feel right to you.',
    },
    q1: 'What is Say It NOW?',
    a1: 'Say It NOW is a web platform that helps you create beautiful, magazine-style digital messages of love and gratitude to share with the people who matter most—while they can still see, hear, and appreciate them.',
    q2: 'Why was Say It NOW created?',
    a2: "Say It NOW was created because too often, we hear how great and loved a person was after they've passed away. People post heartfelt messages on social media to the deceased, but those words were shared too late. Say It NOW helps people know how loved, appreciated, or great they are while they can still feel it.",
    q3: 'Who can I send a Living Announcement to?',
    a3: "You can send a Living Announcement to anyone—parents, grandparents, siblings, children, spouses, friends, mentors, colleagues, or even yourself! If there's someone in your life who deserves to know how much they mean to you, Say It NOW makes it easy to tell them.",
    q4: 'How is Say It NOW different from social media posts?',
    a4: "Unlike social media posts that get lost in feeds, Say It NOW creates beautiful, personalized magazine-style announcements that feel special and intentional. They're designed to be treasured keepsakes, not fleeting posts. Plus, our AI features help you find the right words and can remind you to stay connected.",
    q5: "What's included in the Free plan?",
    a5: 'The Free plan includes 3 free announcements, the ability to upload 1-3 photos, select relationships, 2 AI assist uses, and access to our templates to help craft meaningful messages.',
    q6: 'What AI features are available?',
    a6: 'Our Connection plan includes AI-powered message writing assistance to help you find the right words. Legacy users get AI Auto-Compose, which creates personalized announcements on your behalf and can send them on a schedule you set—a helpful way to stay connected even when life gets busy.',
    q7: 'What are Heartfelt Reminders?',
    a7: 'Heartfelt Reminders are gentle nudges sent after 7 days of inactivity to encourage you to reconnect with your loved ones. Life gets busy, and these reminders can help you remember to express your love. Available in Connection and Legacy plans.',
    q8: 'How does AI Auto-Compose work?',
    a8: 'With AI Auto-Compose (Legacy feature), you set up your recipients with their relationship and any personal notes. Our AI crafts personalized, heartfelt messages based on your relationship. You get a 24-hour preview notification before any message is sent, giving you time to approve, edit, or skip it.',
    q9: 'Is my data private and secure?',
    a9: 'Yes. Your Living Announcements remain private by default. You control who sees them by sharing the unique link. We use industry-standard encryption and do not share your personal information with third parties.',
    q10: 'How do I share a Living Announcement?',
    a10: "Once you create and publish your announcement, you'll receive a unique shareable link. You can send this link directly to your recipient via text, email, or any messaging platform. You can also share it on social media if you want others to see your beautiful message.",
    q11: 'Can I edit or delete my announcements?',
    a11: 'Yes! You have full control over your announcements. You can edit them at any time from your dashboard, and you can delete them if needed. We believe your messages are yours to manage.',
    q12: 'Do credits expire?',
    a12: 'No! Credits never expire. They remain in your account until you use them, so you can express love at your own pace without pressure.',
    q13: 'Can I add more credits later?',
    a13: "Absolutely. You can purchase additional credits or upgrade to a higher bundle anytime. Your existing credits carry over, and new features unlock as you grow with Say It NOW.",
    q14: 'What if I need help?',
    a14: "We're here to help! Legacy users get priority support. All users can reach out to us through our contact form, and we aim to respond within 24-48 hours. We're passionate about helping you express love to the people who matter most.",
    contactTitle: 'Still Have Questions?',
    contactSubtitle: "We're here to help you on your journey to expressing love. Reach out anytime!",
    contactButton: 'Get Started Free',
    contactEmail: 'Contact Support',
  },
  jackey: {
    nudges: {
      firstExpression: "You showed up. That matters more than getting it perfect.",
      relationshipCelebrant: "Some relationships hold words we don't always say out loud. You just did.",
      birthdayChampion: "Future-you will be glad you remembered this.",
      friendshipKeeper: "Long friendships are built from moments like this.",
      consistencyStar: "You're creating a rhythm of presence — that's rare.",
      loveAmbassador: "This isn't a streak. It's a season of showing up.",
      legacyBuilder: "What you're creating now will last longer than the moment.",
    },
  },
  header: { tagline: "While They're Still Here" },
  settings: {
    settings: 'Settings',
    profile: 'Profile',
    preferences: 'Preferences',
    importantDates: 'Important Dates',
    autoCompose: 'Auto-Compose',
    fullName: 'Full Name',
    language: 'Language',
    save: 'Save',
    saving: 'Saving...',
    saved: 'Saved!',
    error: 'Error saving',
  },
  creditDisplay: {
    heroHeadline: 'Who comes to mind today?',
    heroSubtext: 'A few words can mean the world.',
    credits: 'Credits',
    createNew: 'Create New',
    addMore: 'Add More',
    aiAssists: 'AI Assists',
    remaining: 'remaining',
  },
  founder: {
    badge: 'Founding Member',
    indicator: 'Sent by a Founding Member of Say It NOW',
    reminderBanner: "You're still eligible for your Founding Member pricing. Reloading keeps your $12 rate locked in.",
    reminderSecondary: 'No pressure — just a heads up.',
    reload: 'Reload now',
    dismiss: 'Dismiss',
    expiryTitle: 'Thank you for being a Founding Member 💚',
    expiryText: 'Your founding window has closed, but your contributions made a lasting impact.',
    expirySubtext: 'You can continue at standard rates.',
    expiryCta: 'View Plans',
  },
  treslog: {
    inlineHintText: 'Living Announcements help you speak outward. TRESLOG is your private lifetime space to look inward.',
    inlineHintLink: 'Learn about TRESLOG',
    upsellTitle: 'Your Private Lifetime Reflection Space',
    upsellText: 'TRESLOG is your personal journal for deep reflection, mood tracking, and self-discovery.',
    upsellCta: 'Unlock TRESLOG',
  },
  postPublish: {
    title: '💚 You showed up.',
    subtitle: "That's what matters most. Your words are out in the world now.",
    share: 'Share',
    dashboard: 'Go to Dashboard',
  },
  mobile: { startWriting: 'Start Writing', speak: 'Speak' },
  security: { checkRequired: 'Please complete the security check.', checkFailed: 'Security check failed to load.', verificationFailed: 'Security verification failed. Please try again.', retry: 'Retry' },
  reveal: { identityRevealed: '✨ Identity Revealed', sentBy: 'This message was sent by', someoneWhoCares: 'Someone who cares', revealingSoon: 'Identity will be revealed soon…', revealInDays: "The sender's identity will be revealed in {days} days", revealInOneDay: "The sender's identity will be revealed in 1 day" },
};
